/**
 * Aggregate Marketing View
 *
 * Displays marketing ad spend data aggregated across all brands
 * Used when "All Clinics" is selected
 */

import { parseISO, format } from 'date-fns'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts'
import { marketingAPI } from '../services/api'
import { DateRangePicker } from './DateRangePicker'
import { getBrandColor } from '../lib/brandColors'
import { Filter, Check, ChevronDown } from 'lucide-react'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button, Badge
} from './ui'
import DataFreshnessWarning from './DataFreshnessWarning'
import { channelColor } from '../lib/channels'

// Brand colors
const BRAND_COLORS = {
  'mdexam': '#3B82F6',
  'tmates': '#22C55E',
  'rose': '#EC4899',
  'bluestone': '#6366F1',
}

/**
 * Brand Filter Dropdown
 */
function BrandFilter({ brands, selectedBrands, onSelectionChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const allSelected = selectedBrands.length === brands.length
  const noneSelected = selectedBrands.length === 0

  const toggleBrand = (brandId) => {
    if (selectedBrands.includes(brandId)) {
      if (selectedBrands.length > 1) {
        onSelectionChange(selectedBrands.filter(id => id !== brandId))
      }
    } else {
      onSelectionChange([...selectedBrands, brandId])
    }
  }

  const selectAll = () => {
    onSelectionChange(brands.map(b => b.brand))
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="h-8 gap-2"
      >
        <Filter className="h-4 w-4" />
        <span className="hidden sm:inline">
          {allSelected ? 'All Brands' : `${selectedBrands.length} Brand${selectedBrands.length !== 1 ? 's' : ''}`}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-lg shadow-lg z-50">
          <div className="p-2">
            <div className="text-xs font-medium text-muted-foreground px-2 py-1 mb-1">
              Filter Brands
            </div>

            <button
              onClick={selectAll}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted text-left"
            >
              <div className={`w-4 h-4 rounded border flex items-center justify-center ${allSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                {allSelected && <Check className="h-3 w-3 text-primary-foreground" />}
              </div>
              <span className="font-medium">Select All</span>
            </button>

            <div className="border-t border-border my-1" />

            {brands.map((brand) => {
              const isSelected = selectedBrands.includes(brand.brand)
              const isLastSelected = isSelected && selectedBrands.length === 1

              return (
                <button
                  key={brand.brand}
                  onClick={() => toggleBrand(brand.brand)}
                  disabled={isLastSelected}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted text-left ${isLastSelected ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                    {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: brand.color || getBrandColor(brand.brand) }}
                  />
                  <span>{brand.displayName}</span>
                </button>
              )
            })}

            {selectedBrands.length === 1 && (
              <p className="text-xs text-muted-foreground px-2 py-1 mt-1">
                At least one brand must be selected
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Channel colours come from lib/channels, which knows the spend-only channels
// (youtube, bing) this map carried alongside the attributed ones.
const getChannelColor = channelColor

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatNumber(value) {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
  return value.toFixed(0)
}

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-popover-foreground mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="flex justify-between gap-4">
            <span>{entry.name}:</span>
            <span className="font-medium">{formatCurrency(entry.value)}</span>
          </p>
        ))}
      </div>
    )
  }
  return null
}

function PieTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const data = payload[0]
    return (
      <div className="bg-popover text-popover-foreground border border-border rounded-lg shadow-xl p-3 text-sm z-50">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: data.payload.color }} />
          <span className="font-semibold">{data.name}</span>
        </div>
        <div className="text-lg font-bold">{formatCurrency(data.value)}</div>
        <div className="text-xs text-muted-foreground">{data.payload.percent?.toFixed(1)}% of total</div>
      </div>
    )
  }
  return null
}

export default function AggregateMarketingView() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [dateRange, setDateRange] = useState(() => {
    const startParam = searchParams.get('startDate')
    const endParam = searchParams.get('endDate')

    if (startParam && endParam) {
      return { from: parseISO(startParam), to: parseISO(endParam) }
    }

    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 29)
    return { from: start, to: end }
  })

  const [data, setData] = useState(null)
  const [trends, setTrends] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedBrands, setSelectedBrands] = useState([])

  // Initialize selected brands when data loads
  useEffect(() => {
    if (data?.by_brand && selectedBrands.length === 0) {
      setSelectedBrands(data.by_brand.map(b => b.brand))
    }
  }, [data?.by_brand])

  // Filter data based on selected brands
  const filteredData = useMemo(() => {
    if (!data || selectedBrands.length === 0) return data
    if (selectedBrands.length === data.by_brand?.length) return data

    const filteredBrands = data.by_brand.filter(b => selectedBrands.includes(b.brand))

    // Recalculate summary
    const summary = filteredBrands.reduce((acc, brand) => ({
      total_spend: acc.total_spend + brand.spend,
      total_clicks: acc.total_clicks + brand.clicks,
      total_impressions: acc.total_impressions + brand.impressions
    }), { total_spend: 0, total_clicks: 0, total_impressions: 0 })

    summary.cpc = summary.total_clicks > 0 ? summary.total_spend / summary.total_clicks : 0
    summary.cpm = summary.total_impressions > 0 ? (summary.total_spend / summary.total_impressions) * 1000 : 0

    // Recalculate daily with filtered brands
    const daily = data.daily.map(day => {
      const filteredByBrand = {}
      let totalSpend = 0
      let totalClicks = 0
      let totalImpressions = 0

      Object.entries(day.by_brand || {}).forEach(([brandId, brandData]) => {
        if (selectedBrands.includes(brandId)) {
          filteredByBrand[brandId] = brandData
          totalSpend += brandData.spend || 0
          totalClicks += brandData.clicks || 0
          totalImpressions += brandData.impressions || 0
        }
      })

      return {
        ...day,
        total_spend: totalSpend,
        total_clicks: totalClicks,
        total_impressions: totalImpressions,
        by_brand: filteredByBrand
      }
    })

    // Recalculate brand percentages
    const brandsWithPercent = filteredBrands.map(b => ({
      ...b,
      percent_of_total: summary.total_spend > 0 ? (b.spend / summary.total_spend) * 100 : 0
    }))

    return {
      ...data,
      summary,
      by_brand: brandsWithPercent,
      daily
    }
  }, [data, selectedBrands])

  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      fetchData()
    }
  }, [dateRange])

  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      const params = new URLSearchParams(searchParams)
      params.set('startDate', format(dateRange.from, 'yyyy-MM-dd'))
      params.set('endDate', format(dateRange.to, 'yyyy-MM-dd'))
      setSearchParams(params, { replace: true })
    }
  }, [dateRange])

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      const params = {
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd')
      }

      const [spendRes, trendsRes] = await Promise.all([
        marketingAPI.getSpendAggregate(params),
        marketingAPI.getTrendsAggregate({ weeks: 8 })
      ])

      setData(spendRes.data)
      setTrends(trendsRes.data.trends)
    } catch (err) {
      console.error('Error fetching aggregate marketing data:', err)
      setError(err.response?.data?.error || 'Failed to fetch marketing data')
    } finally {
      setLoading(false)
    }
  }

  // Prepare chart data using filtered data
  const brandPieData = filteredData?.by_brand?.map(b => ({
    name: b.displayName,
    value: b.spend,
    color: b.color,
    percent: b.percent_of_total
  })) || []

  const channelPieData = filteredData?.by_channel?.map((ch, index) => ({
    name: ch.name,
    value: ch.spend,
    color: getChannelColor(ch.channel, index),
    percent: ch.percent_of_total
  })) || []

  // Daily stacked area by brand
  const dailyChartData = filteredData?.daily?.map(day => {
    const entry = { date: day.date.substring(5) }
    filteredData.by_brand?.forEach(brand => {
      entry[brand.displayName] = day.by_brand[brand.brand]?.spend || 0
    })
    entry.total = day.total_spend
    return entry
  }) || []

  const trendsChartData = trends?.map(t => ({
    week: t.week,
    spend: t.spend,
    clicks: t.clicks
  })) || []

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Marketing - All Brands</h1>
          <p className="text-muted-foreground mt-1">
            Aggregated ad spend across {data?.brands_included?.length || 0} brands
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.by_brand?.length > 1 && (
            <BrandFilter
              brands={data.by_brand}
              selectedBrands={selectedBrands}
              onSelectionChange={setSelectedBrands}
            />
          )}
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </div>

      <DataFreshnessWarning
        daily={data?.daily}
        dateRange={dateRange}
        loading={loading}
      />

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading aggregate marketing data...</p>
          </div>
        </div>
      ) : filteredData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Spend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(filteredData.summary.total_spend)}</div>
                <p className="text-xs text-muted-foreground">{filteredData.date_range?.days || 0} days</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Clicks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(filteredData.summary.total_clicks)}</div>
                <p className="text-xs text-muted-foreground">{filteredData.by_brand?.length || 0} brands</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Avg CPC</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${filteredData.summary.cpc?.toFixed(2) || '0.00'}</div>
                <p className="text-xs text-muted-foreground">Cost per click</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Impressions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(filteredData.summary.total_impressions)}</div>
                <p className="text-xs text-muted-foreground">CPM: ${filteredData.summary.cpm?.toFixed(2) || '0.00'}</p>
              </CardContent>
            </Card>
          </div>

          {/* Brand and Channel Breakdown Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Spend by Brand */}
            <Card>
              <CardHeader>
                <CardTitle>Spend by Brand</CardTitle>
                <CardDescription>Ad spend distribution across brands</CardDescription>
              </CardHeader>
              <CardContent>
                {brandPieData.length > 0 ? (
                  <div className="h-80 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={brandPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={110}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {brandPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} stroke="hsl(var(--background))" strokeWidth={2} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                        <Legend formatter={(value) => <span className="text-foreground text-sm">{value}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0" style={{ marginBottom: '36px' }}>
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground uppercase">Total</div>
                        <div className="text-xl font-bold text-foreground">{formatCurrency(filteredData.summary.total_spend)}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-80 flex items-center justify-center text-muted-foreground">No data</div>
                )}
              </CardContent>
            </Card>

            {/* Spend by Channel */}
            <Card>
              <CardHeader>
                <CardTitle>Spend by Channel</CardTitle>
                <CardDescription>Ad spend distribution across channels</CardDescription>
              </CardHeader>
              <CardContent>
                {channelPieData.length > 0 ? (
                  <div className="h-80 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={channelPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={110}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {channelPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} stroke="hsl(var(--background))" strokeWidth={2} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                        <Legend formatter={(value) => <span className="text-foreground text-sm">{value}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-80 flex items-center justify-center text-muted-foreground">No data</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Brand Breakdown Table */}
          <Card>
            <CardHeader>
              <CardTitle>Brand Performance</CardTitle>
              <CardDescription>Detailed metrics by brand</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium">Brand</th>
                      <th className="text-right py-2 px-2 font-medium">Spend</th>
                      <th className="text-right py-2 px-2 font-medium">Clicks</th>
                      <th className="text-right py-2 px-2 font-medium">Impressions</th>
                      <th className="text-right py-2 px-2 font-medium">CPC</th>
                      <th className="text-right py-2 px-2 font-medium">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.by_brand?.map((brand) => (
                      <tr key={brand.brand} className="border-b hover:bg-muted/50">
                        <td className="py-2 px-2">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: brand.color }} />
                            <span className="font-medium">{brand.displayName}</span>
                          </div>
                        </td>
                        <td className="text-right py-2 px-2">{formatCurrency(brand.spend)}</td>
                        <td className="text-right py-2 px-2">{formatNumber(brand.clicks)}</td>
                        <td className="text-right py-2 px-2">{formatNumber(brand.impressions)}</td>
                        <td className="text-right py-2 px-2">${brand.cpc?.toFixed(2) || '0.00'}</td>
                        <td className="text-right py-2 px-2">
                          <Badge variant="secondary" className="text-xs">{brand.percent_of_total?.toFixed(1)}%</Badge>
                        </td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr className="font-semibold bg-muted/30">
                      <td className="py-2 px-2">Total</td>
                      <td className="text-right py-2 px-2">{formatCurrency(filteredData.summary.total_spend)}</td>
                      <td className="text-right py-2 px-2">{formatNumber(filteredData.summary.total_clicks)}</td>
                      <td className="text-right py-2 px-2">{formatNumber(filteredData.summary.total_impressions)}</td>
                      <td className="text-right py-2 px-2">${filteredData.summary.cpc?.toFixed(2) || '0.00'}</td>
                      <td className="text-right py-2 px-2">100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Daily Spend by Brand Chart */}
          {dailyChartData.length > 0 && filteredData.by_brand?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Daily Spend by Brand</CardTitle>
                <CardDescription>Stacked area chart showing daily spend distribution</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dailyChartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} tickLine={false} />
                      <YAxis tickFormatter={(value) => `$${formatNumber(value)}`} tick={{ fontSize: 12 }} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      {filteredData.by_brand?.map((brand) => (
                        <Area
                          key={brand.brand}
                          type="monotone"
                          dataKey={brand.displayName}
                          stackId="1"
                          stroke={brand.color}
                          fill={brand.color}
                          fillOpacity={0.6}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Weekly Trends */}
          {trendsChartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Weekly Trends (All Brands)</CardTitle>
                <CardDescription>Spend and click trends over the past 8 weeks</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trendsChartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis yAxisId="left" tickFormatter={(value) => `$${formatNumber(value)}`} tick={{ fontSize: 12 }} tickLine={false} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={formatNumber} tick={{ fontSize: 12 }} tickLine={false} />
                      <Tooltip
                        formatter={(value, name) => [name === 'Spend' ? formatCurrency(value) : formatNumber(value), name]}
                        contentStyle={{ backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="spend" fill="#6366F1" name="Spend" />
                      <Bar yAxisId="right" dataKey="clicks" fill="#10B981" name="Clicks" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
